import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import {
  computeFixedTooltipStyle,
  isInsideTooltipInteraction,
  bindTooltipScrollFollow,
} from '@/utils/fixedTooltipPosition';

export function useFixedInfoTooltip({
  align = 'end',
  offset = 8,
  zIndex = 10000,
  wrapperSelector = '.info-tooltip-wrapper',
  tooltipSelector = '.card-info-tooltip',
  closeOnScroll = false,
} = {}) {
  const showTooltip = ref(false);
  const tooltipStyle = ref({});
  let anchorEl = null;
  let unbindScrollFollow = null;

  const stopScrollFollow = () => {
    unbindScrollFollow?.();
    unbindScrollFollow = null;
  };

  const closeTooltip = () => {
    showTooltip.value = false;
    anchorEl = null;
    stopScrollFollow();
  };

  const repositionTooltip = async () => {
    if (!showTooltip.value || !anchorEl) return;

    await nextTick();
    const tooltipEl = document.querySelector(tooltipSelector);
    const width = tooltipEl?.offsetWidth || 260;
    const height = tooltipEl?.offsetHeight || 80;
    const rect = anchorEl.getBoundingClientRect();

    tooltipStyle.value = {
      ...computeFixedTooltipStyle(rect, {
        align,
        offset,
        zIndex,
        tooltipWidth: width,
        estimatedHeight: height,
      }),
      visibility: 'visible',
    };
  };

  const startScrollFollow = () => {
    stopScrollFollow();
    if (!anchorEl) return;
    unbindScrollFollow = bindTooltipScrollFollow(anchorEl, () => {
      void repositionTooltip();
    });
  };

  const openTooltipAt = async (event) => {
    anchorEl = event.currentTarget;
    showTooltip.value = true;
    tooltipStyle.value = {
      position: 'fixed',
      top: '-9999px',
      left: '0',
      visibility: 'hidden',
      zIndex,
    };

    await repositionTooltip();
    if (closeOnScroll) return;
    startScrollFollow();
  };

  const toggleTooltip = async (event) => {
    if (showTooltip.value) {
      closeTooltip();
      return;
    }

    await openTooltipAt(event);
  };

  const onOutsideClick = (event) => {
    if (!showTooltip.value) return;
    if (isInsideTooltipInteraction(event, { wrapperSelector, tooltipSelector })) return;
    closeTooltip();
  };

  const onScrollOrResize = () => {
    if (!showTooltip.value) return;
    if (closeOnScroll) {
      closeTooltip();
      return;
    }
    void repositionTooltip();
  };

  onMounted(() => {
    document.addEventListener('click', onOutsideClick);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
  });

  onUnmounted(() => {
    document.removeEventListener('click', onOutsideClick);
    window.removeEventListener('resize', onScrollOrResize);
    window.removeEventListener('scroll', onScrollOrResize, true);
    stopScrollFollow();
  });

  return {
    showTooltip,
    tooltipStyle,
    toggleTooltip,
    openTooltipAt,
    closeTooltip,
  };
}
